import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API } from '../utils/helpers';
import useMobileViewport from '../utils/useMobileViewport';

// Customer-facing payment page for textable payment links. Self-contained:
// no app theme, no sidebar, always light, mobile-first — a customer reaching
// this from a text is almost certainly on a phone.
//
// The token in the URL is the credential — knowing it proves you received the
// text. Same pattern as the booking manage page.

const BASE = {
  bg: '#f7f6f3',
  card: '#ffffff',
  border: '#e2e5ea',
  borderSoft: '#eef0f3',
  text: '#1a2233',
  sub: '#5b6472',
  muted: '#9aa4b2',
  accent: '#e67e22',
  headerBg: '#0f1724',
  danger: '#c0392b',
  ok: '#27ae60',
};
let C = { ...BASE };

function PayForm({ amount, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const pay = async () => {
    if (!stripe || !elements) return;
    setError('');
    setProcessing(true);
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });
    if (stripeError) {
      setError(stripeError.message || 'Your card could not be processed. Please try another card.');
      setProcessing(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      onPaid();
      return;
    }
    setError('Payment did not complete. Please try again or call the shop.');
    setProcessing(false);
  };

  return (
    <>
      <PaymentElement
        options={{
          // Hide the Link wallet. Link is NOT a payment_method_type — it rides
          // alongside 'card' and brings its own funding options with it:
          // Instant Bank Payments ("Bank — $5 back") and "Klarna — Powered by
          // Link". That's why pinning the intent to ['card'] never removed
          // them, and why excluded_payment_method_types can't either (Stripe
          // errors on wallets there). The wallets hash is the only lever.
          // applePay/googlePay stay at their default 'auto' — we want those.
          wallets: { link: 'never' },
        }}
      />
      {error && <div style={{ fontSize: 13, color: C.danger, marginTop: 12 }}>{error}</div>}
      <button onClick={pay} disabled={!stripe || processing}
        style={{ width: '100%', background: C.accent, color: '#fff', border: 'none', padding: '14px',
          borderRadius: 10, fontSize: 15, fontWeight: 600, marginTop: 16, fontFamily: 'inherit',
          cursor: processing ? 'default' : 'pointer', opacity: processing ? 0.6 : 1 }}>
        {processing ? 'Processing…' : `Pay $${Number(amount).toFixed(2)}`}
      </button>
      <p style={{ fontSize: 11.5, color: C.muted, textAlign: 'center', margin: '14px 0 0', lineHeight: 1.5 }}>
        Payments are processed securely by Stripe.
      </p>
    </>
  );
}

function PaymentPage() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [justPaid, setJustPaid] = useState(false);

  const token = useMemo(() => new URLSearchParams(window.location.search).get('token'), []);

  // The staff app's index.html has no mobile viewport (it's a desktop tool),
  // which renders this zoomed out on phones. Same fix as the booking page.
  useMobileViewport();

  // The staff app paints the document dark. This is a customer-facing light
  // page, so on mobile — where rubber-band scrolling exposes what's behind the
  // content — the app's dark background shows through below the card. Paint the
  // document for as long as this page is mounted; restore on unmount.
  useEffect(() => {
    const html = document.documentElement;
    const prevHtml = html.style.background;
    const prevBody = document.body.style.background;
    html.style.background = BASE.bg;
    document.body.style.background = BASE.bg;
    return () => {
      html.style.background = prevHtml;
      document.body.style.background = prevBody;
    };
  }, []);

  useEffect(() => {
    if (!token) { setLoadError(true); return; }
    fetch(`${API}/api/payments/pay/${encodeURIComponent(token)}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        if (d.accentColor && /^#[0-9a-fA-F]{6}$/.test(d.accentColor)) {
          C = { ...BASE, accent: d.accentColor };
        }
        setData(d);
        if (d.state === 'payable' && d.stripePublishableKey) {
          setStripePromise(loadStripe(d.stripePublishableKey, { stripeAccount: d.stripeAccountId }));
        }
      })
      .catch(() => setLoadError(true));
  }, [token]);

  const shell = (children) => (
    <div style={{ minHeight: '100dvh', boxSizing: 'border-box', background: C.bg, padding: '24px 12px',
      fontFamily: "'Inter', -apple-system, 'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: 420, margin: '0 auto', background: C.card,
        border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(15,23,36,0.08)' }}>
        <div style={{ background: C.headerBg, padding: '20px 22px', textAlign: 'center' }}>
          {data?.shopLogoUrl
            ? <img src={data.shopLogoUrl} alt={(data.shopName || '').trim()}
                style={{ maxHeight: 52, maxWidth: 240, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
            : <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{(data?.shopName || 'Payment').trim()}</div>}
          <div style={{ fontSize: 12, color: '#9aa4b2', marginTop: 5 }}>Secure payment</div>
        </div>
        <div style={{ height: 3, background: C.accent }} />
        <div style={{ padding: 22 }}>{children}</div>
      </div>
      <div style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: C.muted }}>Powered by Spinboard</div>
    </div>
  );

  if (loadError || (data && data.state === 'error')) return shell(
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>We couldn't find this payment.</div>
      <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.6 }}>
        Please call or text the shop{data?.shopPhone ? ` at ${data.shopPhone}` : ''} for a new link.
      </div>
    </div>
  );

  if (!data) return shell(
    <div style={{ textAlign: 'center', padding: '40px 0', color: C.sub, fontSize: 14 }}>Loading…</div>
  );

  if (data.state === 'expired') return shell(
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 8 }}>This payment link has expired.</div>
      <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.6 }}>
        Please call or text the shop{data.shopPhone ? ` at ${data.shopPhone}` : ''} for a new link.
      </div>
    </div>
  );

  // Shared by the checkout and receipt states — same itemization, rendered
  // once. Defined after the !data guard so data is always present here.
  const invoiceCard = data.invoice ? (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 18, overflow: 'hidden', textAlign: 'left' }}>
      <div style={{ background: C.bg, padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
          {data.invoice.displayId || 'Service invoice'}
        </div>
        {data.invoice.bike?.description && data.invoice.bike.description !== '—' && (
          <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>
            {data.invoice.bike.description}
            {data.invoice.bike.color ? ` · ${data.invoice.bike.color}` : ''}
            {data.invoice.bike.size ? ` · ${data.invoice.bike.size}` : ''}
          </div>
        )}
      </div>

      <div style={{ padding: '4px 14px' }}>
        {(data.invoice.lineItems || []).map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 0',
            borderBottom: i < data.invoice.lineItems.length - 1 ? `1px solid ${C.borderSoft}` : 'none' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: C.text, lineHeight: 1.4 }}>{l.description}</div>
              {l.note && (
                <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.5, marginTop: 2, whiteSpace: 'pre-wrap' }}>{l.note}</div>
              )}
              <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                {l.lineType === 'parts' ? 'Parts' : l.lineType === 'misc' ? 'Miscellaneous' : 'Labor'}
                {Number(l.quantity) > 1 ? ` · ${l.quantity} × $${Number(l.unitPrice).toFixed(2)}` : ''}
              </div>
            </div>
            <div style={{ fontSize: 13, color: C.text, whiteSpace: 'nowrap' }}>
              ${Number(l.subtotal).toFixed(2)}
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.border}`, background: C.bg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: C.sub, marginBottom: 4 }}>
          <span>Subtotal</span><span>${Number(data.invoice.subtotal).toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: C.sub, marginBottom: 4 }}>
          <span>Tax ({Number(data.invoice.taxRate).toFixed(3)}%)</span>
          <span>${Number(data.invoice.taxAmount).toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 600, color: C.text }}>
          <span>Total</span><span>${Number(data.invoice.total).toFixed(2)}</span>
        </div>
      </div>

      {data.invoice.receiptNote && (
        <div style={{ padding: '11px 14px', borderTop: `1px solid ${C.border}`, fontSize: 12.5,
          color: C.sub, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {data.invoice.receiptNote}
        </div>
      )}
    </div>
  ) : null;

  // priorPaid never includes this request, so the receipt adds this payment and
  // the checkout view doesn't. No dependence on webhook timing either way.
  const invTotal = data.invoice ? Number(data.invoice.total) : 0;
  const thisAmount = Number(data.amount);
  const priorPaid = data.priorPaid != null ? Number(data.priorPaid) : 0;
  const hadPriorPayments = priorPaid > 0.01;
  const receivedTotal = priorPaid + thisAmount;          // after this payment
  const remaining = data.invoice ? invTotal - receivedTotal : 0;
  const isPartial = data.invoice && Math.abs(invTotal - thisAmount) > 0.01;
  const remainingAfter = remaining;

  if (justPaid || data.state === 'paid') return shell(
    <div style={{ padding: '8px 0' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#eafaf1', color: C.ok,
          fontSize: 24, lineHeight: '48px', margin: '0 auto 14px' }}>✓</div>
        <div style={{ fontSize: 17, fontWeight: 600, color: C.text }}>Payment received</div>
        <div style={{ fontSize: 24, fontWeight: 600, color: C.text, marginTop: 6 }}>
          ${Number(data.amount).toFixed(2)}
        </div>
        <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, marginBottom: 18 }}>
          {new Date(data.paidAt || Date.now()).toLocaleString('en-US', {
            month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
          })}
          {data.description ? ` · ${data.description}` : ''}
        </div>
      </div>

      {invoiceCard}

      <div style={{ background: C.bg, borderRadius: 10, padding: '13px 15px', textAlign: 'left' }}>
        <div style={{ fontSize: 12.5, color: C.sub, lineHeight: 1.6 }}>
          {isPartial && remaining > 0.01 ? (
            <>Thanks! ${Number(data.amount).toFixed(2)} has been applied to your account.
            {hadPriorPayments ? ` $${receivedTotal.toFixed(2)} has now been received toward your $${invTotal.toFixed(2)} balance, leaving` : ' That leaves a balance of'} ${remaining.toFixed(2)} due at pickup. Your bike is ready during business hours.</>
          ) : (
            <>Thanks! This has been applied to your account and covers your balance in full.
            Your bike is ready for pickup during business hours.</>
          )}
        </div>
      </div>

      <div style={{ fontSize: 11.5, color: C.muted, textAlign: 'center', marginTop: 14, lineHeight: 1.5 }}>
        Keep this link — you can reopen it any time for your receipt.
      </div>
    </div>
  );

  return shell(
    <>
      {data.description && <div style={{ fontSize: 13, color: C.sub }}>{data.description}</div>}
      <div style={{ fontSize: 28, fontWeight: 600, color: C.text, marginTop: 4, marginBottom: 18 }}>
        ${Number(data.amount).toFixed(2)}
      </div>

      {invoiceCard}

      {isPartial && (
        <div style={{ background: C.bg, borderRadius: 8, padding: '10px 13px', marginBottom: 18,
          fontSize: 12.5, color: C.sub, lineHeight: 1.6 }}>
          You're paying <strong style={{ color: C.text }}>${Number(data.amount).toFixed(2)}</strong> toward
          a balance of ${invTotal.toFixed(2)}.
          {hadPriorPayments ? ` $${priorPaid.toFixed(2)} has already been received.` : ''}
          {remainingAfter > 0.01 ? ` $${remainingAfter.toFixed(2)} will remain due at pickup.` : ' This settles your balance.'}
        </div>
      )}

      {stripePromise ? (
        <Elements stripe={stripePromise}
          options={{ clientSecret: data.clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: C.accent } } }}>
          <PayForm amount={data.amount} onPaid={() => { setJustPaid(true); window.scrollTo(0, 0); }} />
        </Elements>
      ) : (
        <div style={{ fontSize: 13, color: C.sub, textAlign: 'center', padding: '20px 0' }}>Loading payment form…</div>
      )}
    </>
  );
}

export default PaymentPage;